import React, { useState, useMemo } from 'react';
import { HeroSettings, ServiceItem, FleetVessel, ContactOffice } from '../../types';
import { 
  useWebsiteHeroSettings, 
  useUpdateWebsiteHeroSetting 
} from '../../hooks/useWebsiteHeroSettings';
import { useServiceItems, useCreateServiceItem, useUpdateServiceItem, useDeleteServiceItem } from '../../hooks/useServiceItems';
import { useFleetVessels, useCreateFleetVessel, useUpdateFleetVessel, useDeleteFleetVessel } from '../../hooks/useFleetVessels';
import { useContactOffices, useCreateContactOffice, useUpdateContactOffice, useDeleteContactOffice } from '../../hooks/useContactOffices';
import HeroSettingsTab from './HeroSettingsTab';
import ServicesTab from './ServicesTab';
import FleetTab from './FleetTab';
import ContactTab from './ContactTab';
import { exportHero, exportServices, exportFleet, exportContacts, printTab } from './websiteExportUtils';

const WebsiteSettings: React.FC = () => {
  const { data: apiHeroSettings = [], isLoading: heroLoading } = useWebsiteHeroSettings();
  const updateHeroMutation = useUpdateWebsiteHeroSetting();

  const { data: apiServices = [], isLoading: servicesLoading, refetch: refetchServices } = useServiceItems();
  const createServiceMutation = useCreateServiceItem(() => refetchServices());
  const updateServiceMutation = useUpdateServiceItem(() => refetchServices());
  const deleteServiceMutation = useDeleteServiceItem(() => refetchServices());

  const { data: apiFleetVessels = [], isLoading: fleetLoading, refetch: refetchFleet } = useFleetVessels();
  const createFleetMutation = useCreateFleetVessel(() => refetchFleet());
  const updateFleetMutation = useUpdateFleetVessel(() => refetchFleet());
  const deleteFleetMutation = useDeleteFleetVessel(() => refetchFleet());

  const { data: apiContactOffices = [], isLoading: contactLoading, refetch: refetchContacts } = useContactOffices();
  const createContactMutation = useCreateContactOffice(() => refetchContacts());
  const updateContactMutation = useUpdateContactOffice(() => refetchContacts());
  const deleteContactMutation = useDeleteContactOffice(() => refetchContacts());

  const heroSettings: HeroSettings = useMemo(() => {
    const h = apiHeroSettings[0];
    if (h) {
      return {
        id: h.documentId || h.id,
        backgroundImage: h.backgroundImageHero?.url || '/images/hero-banner.jpg',
        companyName: h.companyName || '', companySubtitle: h.companySubtitle || '',
        missionLine1: h.missionLine1 || '', missionLine2: h.missionLine2 || '', missionLine3: h.missionLine3 || '',
        primaryButtonText: h.primaryButtonText || '', primaryButtonLink: h.primaryButtonLink || '',
        secondaryButtonText: h.secondaryButtonText || '', secondaryButtonLink: h.secondaryButtonLink || '',
        isActive: h.isActive ?? true, maintenanceMode: h.maintenanceMode ?? false,
        updatedAt: h.updatedAt || '', updatedBy: 'Admin', documentId: h.documentId
      } as any;
    }
    return {
      id: '1', backgroundImage: '/images/hero-banner.jpg',
      companyName: 'PetroMarine', companySubtitle: 'Offshore Projects Support Co.',
      missionLine1: 'We provide quality ship rental services',
      missionLine2: 'for MARITIME OPERATIONS', missionLine3: 'in Egypt',
      primaryButtonText: 'Our Fleet', primaryButtonLink: '/fleet',
      secondaryButtonText: 'Contact Us', secondaryButtonLink: '/contact',
      isActive: true, maintenanceMode: false, updatedAt: '', updatedBy: 'Admin'
    };
  }, [apiHeroSettings]);

  const [activeTab, setActiveTab] = useState<string>('hero');
  const [editableHeroSettings, setEditableHeroSettings] = useState<HeroSettings>(heroSettings);
  const [heroInitialized, setHeroInitialized] = useState(false);
  const [editedServices, setEditedServices] = useState<Record<string, Partial<ServiceItem>>>({});
  const [editedFleet, setEditedFleet] = useState<Record<string, Partial<FleetVessel>>>({});
  const [editedContacts, setEditedContacts] = useState<Record<string, Partial<ContactOffice>>>({});

  React.useEffect(() => {
    if (apiHeroSettings.length > 0 && !heroInitialized) {
      setEditableHeroSettings(heroSettings);
      setHeroInitialized(true);
    }
  }, [heroSettings, apiHeroSettings.length, heroInitialized]);

  const getEditableValue = <T,>(map: Record<string, Partial<T>>, item: any, field: string) => {
    const id = item.documentId || item.id;
    return (map[id] as any)?.[field] ?? item[field] ?? '';
  };
  const setEditableField = <T,>(setter: React.Dispatch<React.SetStateAction<Record<string, Partial<T>>>>, item: any, field: string, value: any) => {
    const id = item.documentId || item.id;
    setter(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const getServiceValue = (s: any, f: string) => getEditableValue(editedServices, s, f);
  const setServiceField = (s: any, f: string, v: any) => setEditableField(setEditedServices, s, f, v);
  const getFleetValue = (v: any, f: string) => getEditableValue(editedFleet, v, f);
  const setFleetField = (v: any, f: string, val: any) => setEditableField(setEditedFleet, v, f, val);
  const getContactValue = (o: any, f: string) => getEditableValue(editedContacts, o, f);
  const setContactField = (o: any, f: string, v: any) => setEditableField(setEditedContacts, o, f, v);

  const handleSaveHeroSettings = async () => {
    try {
      await updateHeroMutation.execute({
        id: editableHeroSettings.id,
        data: {
          companyName: editableHeroSettings.companyName, companySubtitle: editableHeroSettings.companySubtitle,
          missionLine1: editableHeroSettings.missionLine1, missionLine2: editableHeroSettings.missionLine2,
          missionLine3: editableHeroSettings.missionLine3,
          primaryButtonText: editableHeroSettings.primaryButtonText, primaryButtonLink: editableHeroSettings.primaryButtonLink,
          secondaryButtonText: editableHeroSettings.secondaryButtonText, secondaryButtonLink: editableHeroSettings.secondaryButtonLink,
          isActive: editableHeroSettings.isActive, maintenanceMode: editableHeroSettings.maintenanceMode ?? false,
          backgroundImage: editableHeroSettings.backgroundImage || '/images/hero-banner.jpg',
          documentId: (editableHeroSettings as any).documentId
        }
      });
      alert('Hero settings saved successfully!');
    } catch (error) { alert('Failed to save hero settings'); console.error(error); }
  };

  const handleAddService = async () => {
    try { await createServiceMutation.execute({ data: { icon: '⚓', title: 'New Service', description: 'Service description', displayOrder: apiServices.length + 1, isActive: true } }); }
    catch (error) { alert('Failed to add service'); console.error(error); }
  };

  const handleDeleteService = async (service: any) => {
    if (!window.confirm('Delete this service?')) return;
    try { await deleteServiceMutation.execute(service.documentId || service.id); }
    catch (error) { alert('Failed to delete service'); console.error(error); }
  };

  const handleSaveAllServices = async () => {
    for (const id of Object.keys(editedServices)) {
      const service = (apiServices as any[]).find((s: any) => (s.documentId || s.id) === id);
      if (service) {
        try { await updateServiceMutation.execute({ id, data: { ...editedServices[id], documentId: service.documentId } }); setEditedServices(prev => { const n = { ...prev }; delete n[id]; return n; }); }
        catch (error) { alert('Failed to save service'); console.error(error); }
      }
    }
    alert('All services saved!');
  };

  const handleAddFleetVessel = async () => {
    try { await createFleetMutation.execute({ data: { name: 'New Vessel', type: 'Type', icon: '🚢', description: 'Vessel description', features: ['Feature 1', 'Feature 2'], displayOrder: apiFleetVessels.length + 1, isActive: true } }); }
    catch (error) { alert('Failed to add vessel'); console.error(error); }
  };

  const handleDeleteFleetVessel = async (vessel: any) => {
    if (!window.confirm('Delete this vessel?')) return;
    try { await deleteFleetMutation.execute(vessel.documentId || vessel.id); }
    catch (error) { alert('Failed to delete vessel'); console.error(error); }
  };

  const handleSaveAllFleet = async () => {
    for (const id of Object.keys(editedFleet)) {
      const vessel = (apiFleetVessels as any[]).find((v: any) => (v.documentId || v.id) === id);
      if (vessel) { try { await updateFleetMutation.execute({ id, data: { ...editedFleet[id], documentId: vessel.documentId } }); } catch (error) { console.error(error); } }
    }
    setEditedFleet({}); alert('Fleet settings saved!');
  };

  const handleAddContactOffice = async () => {
    try { await createContactMutation.execute({ data: { name: 'New Office', officeType: 'branch', country: 'Egypt', city: 'City', addressLine1: 'Address', phoneNumbers: ['+20 X XXXX XXXX'], email: 'office@petromarine.com', displayOrder: apiContactOffices.length + 1, isActive: true } }); }
    catch (error) { alert('Failed to add office'); console.error(error); }
  };

  const handleDeleteContactOffice = async (office: any) => {
    if (!window.confirm('Delete this office?')) return;
    try { await deleteContactMutation.execute(office.documentId || office.id); }
    catch (error) { alert('Failed to delete office'); console.error(error); }
  };

  const handleSaveAllContacts = async () => {
    for (const id of Object.keys(editedContacts)) {
      const office = (apiContactOffices as any[]).find((o: any) => (o.documentId || o.id) === id);
      if (office) { try { await updateContactMutation.execute({ id, data: { ...editedContacts[id], documentId: office.documentId } }); } catch (error) { console.error(error); } }
    }
    setEditedContacts({}); alert('Contact settings saved!');
  };

  const handleExport = () => {
    const map: Record<string, () => void> = {
      hero: () => exportHero(heroSettings), services: () => exportServices(apiServices as any[]),
      fleet: () => exportFleet(apiFleetVessels as any[]), contact: () => exportContacts(apiContactOffices as any[])
    };
    map[activeTab]?.();
  };

  const handlePrint = () => printTab(activeTab, heroSettings, apiServices as any[], apiFleetVessels as any[], apiContactOffices as any[]);

  const tabs = [
    { id: 'hero', label: 'Hero Section', icon: '🏠' },
    { id: 'services', label: 'Services', icon: '⚙️' },
    { id: 'fleet', label: 'Fleet Management', icon: '🚢' },
    { id: 'contact', label: 'Contact Info', icon: '📞' }
  ];

  const isLoading = heroLoading || servicesLoading || fleetLoading || contactLoading;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Website Settings</h1>
        <p className="text-gray-600 mt-1">Manage all website content and configurations</p>
      </div>

      {isLoading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <p className="text-blue-800">Loading website settings from API...</p>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {tabs.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-4 px-6 text-center font-medium text-sm transition-colors ${activeTab === tab.id ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'}`}>
                <span className="mr-2">{tab.icon}</span>{tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          <div className="flex justify-end space-x-3 mb-6">
            <button onClick={handleExport} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500">Export</button>
            <button onClick={handlePrint} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">Print</button>
          </div>

          {activeTab === 'hero' && <HeroSettingsTab settings={editableHeroSettings} onChange={setEditableHeroSettings} onSave={handleSaveHeroSettings} isSaving={updateHeroMutation.isLoading} />}
          {activeTab === 'services' && <ServicesTab services={apiServices as any[]} getServiceValue={getServiceValue} setServiceField={setServiceField} onAdd={handleAddService} onDelete={handleDeleteService} onSaveAll={handleSaveAllServices} isAdding={createServiceMutation.isLoading} isSaving={updateServiceMutation.isLoading} />}
          {activeTab === 'fleet' && <FleetTab vessels={apiFleetVessels as any[]} getFleetValue={getFleetValue} setFleetField={setFleetField} onAdd={handleAddFleetVessel} onDelete={handleDeleteFleetVessel} onSaveAll={handleSaveAllFleet} isAdding={createFleetMutation.isLoading} isSaving={updateFleetMutation.isLoading} />}
          {activeTab === 'contact' && <ContactTab offices={apiContactOffices as any[]} getContactValue={getContactValue} setContactField={setContactField} onAdd={handleAddContactOffice} onDelete={handleDeleteContactOffice} onSaveAll={handleSaveAllContacts} isAdding={createContactMutation.isLoading} isSaving={updateContactMutation.isLoading} />}
        </div>
      </div>
    </div>
  );
};

export default WebsiteSettings;
